import { appUrl } from './template';

/**
 * Resolução de links vindos do CMS (banners, stories, menu horizontal).
 *
 * O critério antigo era `hostname.includes('apostatudo')`, que classificava
 * qualquer subdomínio como interno — inclusive o encurtador
 * (`go.apostatudo.bet.br/hCkd/9pmc0xgh`), que virava a rota inexistente
 * `/hCkd/9pmc0xgh`. Aqui a comparação é por host exato: só o host do próprio
 * app (qualquer ambiente, via NUXT_APP_URL) e o domínio raiz de produção
 * contam como internos, então `go.`, `blog.`, `promo.` etc. são externos.
 *
 * A exceção do domínio de produção existe pra link cadastrado no CMS de prod
 * continuar navegando internamente quando aberto em homolog/dev.
 *
 * Nada aqui usa `window`: o resultado precisa ser igual no SSR e no client
 * pra não gerar mismatch de hidratação nos atributos do NuxtLink.
 */
const APP_ROOT_DOMAIN = 'apostatudo.bet.br';

const normalizeHost = (host: string): string =>
  host
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    ?.split(':')[0]
    ?.replace(/^www\./, '') ?? '';

const internalHosts = (): string[] => {
  const hosts = [APP_ROOT_DOMAIN];

  try {
    const configured = normalizeHost(appUrl() ?? '');

    if (configured) hosts.push(configured);
  } catch {
    // Fora do contexto do Nuxt (runtimeConfig indisponível): só o domínio raiz.
  }

  return hosts;
};

const isAbsoluteUrl = (linkUrl: string): boolean =>
  linkUrl.startsWith('http://') || linkUrl.startsWith('https://');

/**
 * `true` apenas quando o link é uma URL absoluta cujo host não é do app.
 * Paths relativos e URLs inválidas são tratados como internos.
 */
export const isExternalAppUrl = (linkUrl?: string | null): boolean => {
  if (!linkUrl) return false;
  if (!isAbsoluteUrl(linkUrl)) return false;

  try {
    return !internalHosts().includes(normalizeHost(new URL(linkUrl).hostname));
  } catch {
    return true;
  }
};

/**
 * Converte URL absoluta do próprio app no path equivalente (pra navegação
 * client-side). Link externo ou path relativo volta inalterado.
 */
export const resolveAppLink = (linkUrl?: string | null): string => {
  if (!linkUrl) return '/';
  if (!isAbsoluteUrl(linkUrl)) return linkUrl;

  try {
    const url = new URL(linkUrl);

    if (internalHosts().includes(normalizeHost(url.hostname))) {
      return url.pathname + url.search + url.hash;
    }

    return linkUrl;
  } catch {
    return linkUrl;
  }
};
